// Revendas, caixas, aliases, entregas, quarentena e auditoria.
//
// Juntas num arquivo porque são a MESMA tela com colunas diferentes: listar
// do que a API devolve, já filtrado pelo escopo de quem entrou. Separá-las
// em seis arquivos quase idênticos é como elas divergem em estado vazio, em
// paginação e em como mostram erro.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import {
  aliases,
  auditoria,
  caixas,
  dominios,
  entregas,
  migracao as migracaoApi,
  quarentena,
  revendas,
} from "../api/admin";
import { ErroDaAPI, explicar } from "../api/problema";
import type { Alias, Caixa, Dominio, Revenda } from "../api/tipos";
import { useSessao } from "../sessao/contexto";
import { dataCurta, tamanho } from "../ui/formato";
import { Icone } from "../ui/Icone";
import {
  Botao,
  Cabecalho,
  Celula,
  ConfirmarRemocao,
  Estado,
  Indicador,
  Linha,
  Paginacao,
  Selo,
  Tabela,
  useFiltroNaURL,
  usePagina,
} from "./comuns";
import { rotuloDaAcao, rotuloDoNivel, rotuloDoTipoDeEntrega } from "./rotulos";

// ---------------------------------------------------------------------------

/**
 * A nota em que o antispam RECUSA na conversa SMTP.
 *
 * Vem de `rspamd/conf/local.d/actions.conf` (`reject = 15`). Está escrito
 * aqui porque "5.8" sozinho não diz nada: a pessoa que opera não sabe se é
 * quase spam ou quase limpo, e a coluna "Pontos" virava enfeite.
 *
 * Se o limiar mudar lá, muda aqui. É duplicação consciente de UM número, em
 * troca de a tela parar de mentir por omissão — a alternativa é a API expor
 * a configuração do Rspamd, que é superfície nova por um inteiro.
 */
const NOTA_DE_RECUSA = 15;

export function Revendas() {
  const fila = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState("");
  const [editando, setEditando] = useState<string | null>(null);

  // DEFEITO REAL, relatado pelo Jhou: a tabela mostrava "Limite de domínios" e
  // "Limite de caixas" e NÃO HAVIA ONDE PREENCHER. Pior: o formulário mandava
  // só o nome, e `email_contato` é obrigatório na API — criar revenda pelo
  // painel respondia 422 e nunca funcionou.
  //
  // Os limites são aplicados de verdade (dominios.go e caixas.go recusam com
  // 409 ao estourar), então o que faltava era só a tela. Mostrar um limite que
  // não se pode definir é pior que não mostrar: parece configurado.
  const [nova, setNova] = useState({
    nome: "",
    email_contato: "",
    limite_dominios: "",
    limite_caixas: "",
  });

  const [pagina, setPagina] = usePagina();
  const lista = useQuery({
    queryKey: ["p", "revendas", "lista", pagina],
    queryFn: () => revendas.listar({ pagina, por_pagina: 50 }),
  });

  const mensagemDoErro = (e: unknown) =>
    e instanceof ErroDaAPI ? (Object.values(e.porCampo)[0]?.[0] ?? e.mensagem) : explicar(e);

  const criar = useMutation({
    mutationFn: () =>
      revendas.criar({
        nome: nova.nome.trim(),
        // O slug vem do nome: pedir os dois ao operador é pedir que ele
        // invente um identificador que a plataforma sabe derivar.
        slug: comoSlug(nova.nome),
        email_contato: nova.email_contato.trim().toLowerCase(),
        // Vazio vira 0, que é "sem limite" — e é o que a pessoa quer dizer
        // quando deixa o campo em branco. Mandar `undefined` daria no mesmo
        // no servidor, mas assim o valor enviado é o valor pretendido.
        limite_dominios: numeroOuZero(nova.limite_dominios),
        limite_caixas: numeroOuZero(nova.limite_caixas),
      } as never),
    onSuccess: () => {
      setNova({ nome: "", email_contato: "", limite_dominios: "", limite_caixas: "" });
      setCriando(false);
      setErro("");
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  return (
    <>
      <Cabecalho
        titulo="Revendas"
        descricao="Quem revende a plataforma, com o limite contratado de cada uma."
        acao={
          <Botao
            icone="Plus"
            aoClicar={() => {
              setErro("");
              setCriando((c) => !c);
            }}
          >
            Nova revenda
          </Botao>
        }
      />

      {criando && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate();
          }}
          className="mb-5 grid gap-3 rounded-xl border border-linha bg-fundo p-4 sm:grid-cols-2"
        >
          <Campo
            rotulo="Nome da revenda"
            valor={nova.nome}
            aoMudar={(v) => setNova((n) => ({ ...n, nome: v }))}
            dica={nova.nome.trim() ? `identificador: ${comoSlug(nova.nome)}` : undefined}
          />
          <Campo
            rotulo="E-mail de contato"
            tipo="email"
            valor={nova.email_contato}
            aoMudar={(v) => setNova((n) => ({ ...n, email_contato: v }))}
            dica="Obrigatório. É para onde vão os avisos da revenda."
          />
          <Campo
            rotulo="Limite de domínios"
            tipo="number"
            valor={nova.limite_dominios}
            aoMudar={(v) => setNova((n) => ({ ...n, limite_dominios: v }))}
            dica="Vazio ou 0 = sem limite."
          />
          <Campo
            rotulo="Limite de caixas"
            tipo="number"
            valor={nova.limite_caixas}
            aoMudar={(v) => setNova((n) => ({ ...n, limite_caixas: v }))}
            dica="Vazio ou 0 = sem limite. Vale para a revenda inteira."
          />
          <div className="flex items-center gap-2 sm:col-span-2">
            <Botao tipo="submit" icone="Plus" ocupado={criar.isPending}>
              Criar
            </Botao>
            <Botao tom="neutro" aoClicar={() => setCriando(false)}>
              Cancelar
            </Botao>
            {erro && <p className="text-sm text-perigo">{erro}</p>}
          </div>
        </form>
      )}

      <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
        <Tabela
          colunas={["Revenda", "Situação", "Domínios", "Caixas", "Criada", ""]}
        >
          {lista.data?.itens?.map((r) => (
            <LinhaDaRevenda
              key={r.uuid}
              revenda={r}
              editando={editando === r.uuid}
              aoEditar={() => setEditando(editando === r.uuid ? null : (r.uuid ?? null))}
              aoFechar={() => setEditando(null)}
            />
          ))}
        </Tabela>
      </Estado>
      <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="revendas" />
    </>
  );
}

/**
 * Uma revenda na tabela, com o formulário de edição embutido.
 *
 * Os limites são editáveis DEPOIS de criada porque plano contratado muda —
 * e sem isto a única saída era recriar a revenda, o que exigiria transferir
 * ou apagar os domínios dela antes.
 */
function LinhaDaRevenda({
  revenda,
  editando,
  aoEditar,
  aoFechar,
}: {
  revenda: Revenda;
  editando: boolean;
  aoEditar: () => void;
  aoFechar: () => void;
}) {
  const fila = useQueryClient();
  const uso = revenda.uso ?? {};
  // O tipo do status vem do CONTRATO, não de string solta: assim, se a lista
  // de situações mudar no openapi.yaml, é aqui que o TypeScript reclama —
  // em vez de a tela mandar um valor que a API recusa em produção.
  type Situacao = NonNullable<Revenda["status"]>;
  const [campos, setCampos] = useState({
    limite_dominios: String(revenda.limite_dominios ?? 0),
    limite_caixas: String(revenda.limite_caixas ?? 0),
    status: (revenda.status ?? "ativo") as Situacao,
  });

  const salvar = useMutation({
    mutationFn: () =>
      revendas.alterar(revenda.uuid!, {
        limite_dominios: numeroOuZero(campos.limite_dominios),
        limite_caixas: numeroOuZero(campos.limite_caixas),
        status: campos.status,
      } as never),
    onSuccess: () => {
      aoFechar();
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
  });

  return (
    <>
      <Linha>
        <Celula>
          <span className="text-tinta">{revenda.nome}</span>
          <span className="block text-xs text-tinta-fraca">{revenda.email_contato}</span>
        </Celula>
        <Celula>
          <Selo
            texto={revenda.status ?? "?"}
            tom={revenda.status === "ativo" ? "ok" : "aviso"}
          />
        </Celula>
        {/* Uso CONTRA limite, não o limite sozinho. "3" não diz nada; "3 de
            10" diz se está perto de acabar, que é a pergunta de quem opera. */}
        <Celula>
          <Uso usado={uso.dominios} limite={revenda.limite_dominios} />
        </Celula>
        <Celula>
          <Uso usado={uso.caixas} limite={revenda.limite_caixas} />
        </Celula>
        <Celula>{dataCurta(revenda.criado_em)}</Celula>
        <Celula>
          <button onClick={aoEditar} className="text-xs text-acao underline">
            {editando ? "fechar" : "editar limites"}
          </button>
        </Celula>
      </Linha>

      {editando && (
        <tr>
          <td colSpan={6} className="bg-fundo px-4 py-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                salvar.mutate();
              }}
              className="grid gap-3 sm:grid-cols-3"
            >
              <Campo
                rotulo="Limite de domínios"
                tipo="number"
                valor={campos.limite_dominios}
                aoMudar={(v) => setCampos((c) => ({ ...c, limite_dominios: v }))}
                dica="0 = sem limite."
              />
              <Campo
                rotulo="Limite de caixas"
                tipo="number"
                valor={campos.limite_caixas}
                aoMudar={(v) => setCampos((c) => ({ ...c, limite_caixas: v }))}
                dica="0 = sem limite."
              />
              <label className="text-xs text-tinta-fraca">
                Situação
                <select
                  value={campos.status}
                  onChange={(e) =>
                    setCampos((c) => ({ ...c, status: e.target.value as Situacao }))
                  }
                  className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
                >
                  <option value="ativo">ativo</option>
                  <option value="suspenso">suspenso</option>
                  <option value="cancelado">cancelado</option>
                </select>
                {/* Suspender a revenda derruba a ENTREGA dos domínios dela: as
                    visões que o Postfix e o Dovecot leem já filtram por status.
                    Não é um rótulo — é uma chave que corta o e-mail. */}
                <span className="mt-1 block">
                  Suspender ou cancelar PARA a entrega de todos os domínios desta
                  revenda, na hora.
                </span>
              </label>
              <div className="flex items-center gap-2 sm:col-span-3">
                <Botao tipo="submit" icone="CheckCircle2" ocupado={salvar.isPending}>
                  Salvar
                </Botao>
                <Botao tom="neutro" aoClicar={aoFechar}>
                  Cancelar
                </Botao>
                {salvar.error && (
                  <p className="text-sm text-perigo">
                    {salvar.error instanceof ErroDaAPI
                      ? (Object.values(salvar.error.porCampo)[0]?.[0] ?? salvar.error.mensagem)
                      : explicar(salvar.error)}
                  </p>
                )}
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

/** "3 de 10", ou "3" quando não há teto. O aviso aparece ao encostar nele. */
function Uso({ usado, limite }: { usado?: number; limite?: number }) {
  const n = usado ?? 0;
  if (!limite) return <span className="tabular-nums">{n}</span>;
  const cheio = n >= limite;
  return (
    <span className={`tabular-nums ${cheio ? "text-perigo" : ""}`}>
      {n} de {limite}
      {cheio && <span className="ml-1 text-xs">(no limite)</span>}
    </span>
  );
}

/** Um campo rotulado com dica embaixo. As telas do painel repetiam isto. */
function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = "text",
  dica,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  tipo?: "text" | "email" | "number";
  dica?: string;
}) {
  return (
    <label className="text-xs text-tinta-fraca">
      {rotulo}
      <input
        type={tipo}
        min={tipo === "number" ? 0 : undefined}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
      />
      {dica && <span className="mt-1 block">{dica}</span>}
    </label>
  );
}

/** Vazio é "sem limite", que no banco é 0 — não é ausência de valor. */
function numeroOuZero(v: string): number {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** O identificador da revenda, derivado do nome. */
function comoSlug(nome: string): string {
  return nome
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// ---------------------------------------------------------------------------

/**
 * Gera uma senha inicial forte.
 *
 * Evita os caracteres que confundem ao ditar por telefone (l/I/1, O/0) —
 * porque é exatamente assim que ela vai ser entregue ao usuário na maioria
 * das vezes. E vem em blocos: ditar 20 caracteres corridos não funciona.
 */
function senhaInicial(): string {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bloco = () =>
    Array.from(crypto.getRandomValues(new Uint32Array(5)))
      .map((n) => a[n % a.length])
      .join("");
  return [bloco(), bloco(), bloco(), bloco()].join("-");
}

// ehCatchAll: origem sem parte local recebe TUDO o que não casa com caixa.
function ehCatchAll(origem: string): boolean {
  const limpa = origem.trim();
  return limpa.startsWith("@") && limpa.length > 1;
}

function contarDestinos(destino: string): number {
  return destino.split(/[,;\n]/).filter((d) => d.trim() !== "").length;
}

export function Caixas() {
  const fila = useQueryClient();
  const { podeVer } = useSessao();
  const [busca, setBusca] = useState("");
  // A página volta a 1 quando o filtro muda: filtrar estando na página 3
  // mostraria uma tela vazia, e a pessoa conclui que a busca não achou nada.
  const [pagina, setPagina] = usePagina(busca);
  const [paginaAliases, setPaginaAliases] = usePagina();
  const [criando, setCriando] = useState<"caixa" | "alias" | null>(null);
  const [erro, setErro] = useState("");
  const [nova, setNova] = useState({
    email: "",
    nome: "",
    senha: "",
    cota_gb: "",
    nivel: "usuario",
  });
  const [novoAlias, setNovoAlias] = useState({ origem: "", destino: "" });
  // `motivo` porque a MESMA faixa serve a dois eventos diferentes, e dizer
  // "criada" numa redefinição é mentir sobre o que acabou de acontecer —
  // além de esconder o efeito que só a redefinição tem: derrubar as sessões.
  const [senhaMostrada, setSenhaMostrada] = useState<
    { email: string; senha: string; motivo: "criada" | "redefinida" } | null
  >(null);
  const lista = useQuery({
    queryKey: ["p", "caixas", "lista", busca, pagina],
    // `q`, não `busca`: é o nome que o contrato declara e o servidor lê. Com
    // `busca` o parâmetro era descartado em silêncio e a lista vinha inteira.
    queryFn: () => caixas.listar({ q: busca || undefined, pagina, por_pagina: 50 }),
  });
  // Os domínios que esta pessoa administra — são os grupos da tela.
  const listaDominios = useQuery({
    queryKey: ["p", "dominios", "para-agrupar"],
    queryFn: () => dominios.listar({ por_pagina: 200 }),
  });
  // O mínimo da senha inicial depende do domínio DIGITADO no endereço: 12, ou
  // 4 com senha simples (migração 60). Domínio fora da lista fica com 12.
  const dominioDaNova = (nova.email.split("@")[1] ?? "").trim().toLowerCase();
  const minimoDaNova =
    listaDominios.data?.itens?.find((d) => d.nome === dominioDaNova)?.senha_minima ?? 12;

  // MAILDIRS ÓRFÃOS. Só o super admin vê: é ele quem decide o destino de
  // e-mail de gente que já saiu.
  const orfaos = useQuery({
    queryKey: ["p", "orfaos"],
    queryFn: () => migracaoApi.orfaos(),
    enabled: podeVer("super_admin"),
  });

  const liberar = useMutation({
    mutationFn: (uuid: string) => migracaoApi.liberarOrfao(uuid),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ["p"] }),
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const listaAliases = useQuery({
    queryKey: ["p", "aliases", "lista", paginaAliases],
    queryFn: () => aliases.listar({ pagina: paginaAliases, por_pagina: 50 }),
  });

  // A caixa escolhida para remoção. Guarda o objeto, não o uuid: o diálogo
  // precisa mostrar E exigir o endereço, e uma lista filtrada que muda embaixo
  // não pode trocar o alvo depois de a pessoa ter lido qual era.
  const [aRemover, setARemover] = useState<{ uuid: string; email: string } | null>(null);
  const [editando, setEditando] = useState<string | null>(null);

  const remover = useMutation({
    mutationFn: (c: { uuid: string; email: string }) => caixas.remover(c.uuid, c.email),
    onSuccess: () => {
      setARemover(null);
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
  });

  const mensagemDoErro = (e: unknown) =>
    e instanceof ErroDaAPI ? (Object.values(e.porCampo)[0]?.[0] ?? e.mensagem) : explicar(e);

  const criarCaixa = useMutation({
    mutationFn: () =>
      caixas.criar({
        email: nova.email.trim(),
        nome_exibicao: nova.nome.trim() || undefined,
        senha: nova.senha,
        // Vazio NÃO é zero: vazio herda a cota do domínio, zero é ilimitada.
        cota_bytes: nova.cota_gb.trim() === "" ? undefined : emBytes(nova.cota_gb),
        // `usuario` é o padrão da API; mandar o campo só quando há promoção
        // mantém a criação comum idêntica ao que era.
        nivel: nova.nivel === "usuario" ? undefined : nova.nivel,
      } as never),
    onSuccess: () => {
      // A senha aparece UMA vez, e a tela a segura até o operador dizer que
      // anotou: ela não pode ser mostrada de novo, e sumir sozinha é como
      // alguém cria uma caixa que ninguém consegue usar.
      setSenhaMostrada({ email: nova.email.trim(), senha: nova.senha, motivo: "criada" });
      setNova({ email: "", nome: "", senha: "", cota_gb: "", nivel: "usuario" });
      setErro("");
      setCriando(null);
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  // VÁRIOS DESTINOS DE UMA VEZ.
  //
  // O cPanel permite `contato@x: contato@x.com,contato@y.com.br`, e a
  // migração já trata isso criando uma linha por destino — ignorar a vírgula
  // faria o cliente parar de receber em metade dos endereços, em silêncio.
  //
  // O formulário, porém, criava UM destino e nada dizia que dá para repetir
  // a origem. Quem precisasse de dois concluiria que não dá. Aqui a vírgula
  // (ou a quebra de linha) faz o que quem digitou espera: um encaminhador
  // por destino, que é exatamente como o banco guarda.
  const criarAlias = useMutation({
    mutationFn: async () => {
      const origem = novoAlias.origem.trim();
      const destinos = novoAlias.destino
        .split(/[,;\n]/)
        .map((d) => d.trim())
        .filter((d) => d !== "");
      if (destinos.length === 0) {
        throw new Error("informe ao menos um destino");
      }
      // EM SEQUÊNCIA, e não em paralelo: se o terceiro falhar, os dois
      // primeiros já existem, e a tela precisa dizer QUAL falhou. Disparar
      // tudo junto embaralharia a ordem dos erros.
      for (const destino of destinos) {
        await aliases.criar({ origem, destino } as never);
      }
    },
    onSuccess: () => {
      setNovoAlias({ origem: "", destino: "" });
      setErro("");
      setCriando(null);
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const pendentes = orfaos.data?.itens ?? [];

  return (
    <>
      <Cabecalho
        titulo="Caixas e encaminhadores"
        descricao="As contas que você administra, e os endereços que apenas repassam."
        acao={
          podeVer("admin_dominio") ? (
            <span className="flex gap-2">
              <Botao
                icone="UserPlus"
                aoClicar={() => {
                  setErro("");
                  setNova((n) => ({ ...n, senha: senhaInicial() }));
                  setCriando(criando === "caixa" ? null : "caixa");
                }}
              >
                Nova caixa
              </Botao>
              <Botao
                tom="neutro"
                icone="Mail"
                aoClicar={() => {
                  setErro("");
                  setCriando(criando === "alias" ? null : "alias");
                }}
              >
                Novo encaminhador
              </Botao>
            </span>
          ) : undefined
        }
      />

      {/* MAILDIRS ÓRFÃOS.
          Aparece AQUI, e não numa tela própria, porque é aqui que a pessoa
          esbarra no problema: ela apaga uma caixa, tenta recriar o mesmo
          endereço, e recebe uma recusa. Pôr a solução noutra tela seria
          pedir que ela adivinhasse onde procurar. */}
      {pendentes.length > 0 && (
        <div className="mb-5 rounded-xl border border-aviso/50 bg-aviso/10 p-4">
          <p className="text-sm font-medium text-tinta">
            {pendentes.length === 1
              ? "1 caixa removida cujo e-mail continua em disco"
              : `${pendentes.length} caixas removidas cujo e-mail continua em disco`}
          </p>
          <p className="mt-1 text-sm text-tinta-media">
            O endereço fica travado até alguém decidir o destino do e-mail. É
            proteção: sem ela, uma pessoa nova com o mesmo endereço herdaria a
            correspondência da anterior.
          </p>
          <ul className="mt-3 space-y-2">
            {pendentes.map((o) => (
              <li
                key={o.uuid}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-tinta">{o.email}</span>
                  <span className="block truncate font-mono text-xs text-tinta-fraca">
                    {o.caminho}
                  </span>
                  <span className="block text-xs text-tinta-fraca">
                    removida em {dataCurta(o.criado_em)}
                    {o.removido_por ? ` por ${o.removido_por}` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={liberar.isPending}
                  onClick={() => liberar.mutate(o.uuid!)}
                  className="rounded-full border border-linha px-3 py-1.5 text-xs text-tinta-media disabled:opacity-40"
                >
                  liberar o endereço
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-tinta-fraca">
            <strong>Liberar não apaga nada.</strong> O e-mail continua em disco, e
            quem receber o endereço vai encontrar a caixa da pessoa anterior —
            faça isso quando for a MESMA pessoa voltando, ou depois de já ter
            salvo o que interessava.
            <br />
            Apagar o e-mail em definitivo não está aqui de propósito: não tem
            desfazer, e a diferença entre as duas ações é fácil demais de errar
            num clique. É no servidor, com{" "}
            <code className="font-mono">make maildirs-purgar EMAIL=…</code>, que
            exige digitar a confirmação por extenso.
          </p>
        </div>
      )}

      {senhaMostrada && (
        <div className="mb-5 rounded-xl border border-aviso/50 bg-aviso/10 p-4">
          <p className="text-sm text-tinta">
            {senhaMostrada.motivo === "criada" ? (
              <>
                Caixa <strong>{senhaMostrada.email}</strong> criada.
              </>
            ) : (
              <>
                Senha de <strong>{senhaMostrada.email}</strong> redefinida. Todas as
                sessões abertas e as senhas de aplicativo dela foram derrubadas.
              </>
            )}{" "}
            Esta senha aparece <strong>uma única vez</strong> — depois daqui só existe
            o hash, e nem nós conseguimos mostrá-la de novo.
          </p>
          <p className="my-3 select-all rounded border border-linha bg-white px-3 py-2 font-mono text-base tracking-wider">
            {senhaMostrada.senha}
          </p>
          <p className="mb-3 text-xs text-tinta-fraca">
            Peça à pessoa que troque no primeiro acesso: esta senha passou pela sua
            tela e provavelmente por uma conversa.
          </p>
          <Botao tom="neutro" aoClicar={() => setSenhaMostrada(null)}>
            Anotei
          </Botao>
        </div>
      )}

      {criando === "caixa" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            criarCaixa.mutate();
          }}
          className="mb-5 grid gap-2 rounded-xl border border-linha bg-fundo p-4 sm:grid-cols-[1fr_1fr_10rem_auto]"
        >
          <input
            value={nova.email}
            onChange={(e) => setNova((n) => ({ ...n, email: e.target.value }))}
            placeholder="pessoa@dominio.com.br"
            aria-label="Endereço da caixa"
            autoFocus
            className="rounded-lg border border-linha px-3 py-2 text-sm"
          />
          <input
            value={nova.nome}
            onChange={(e) => setNova((n) => ({ ...n, nome: e.target.value }))}
            placeholder="Nome de exibição (opcional)"
            aria-label="Nome de exibição"
            className="rounded-lg border border-linha px-3 py-2 text-sm"
          />
          {/* A cota na CRIAÇÃO, e não só na edição: criar dez caixas e depois
              abrir dez vezes para pôr o mesmo número é o tipo de trabalho que
              faz o operador simplesmente não pôr cota nenhuma. */}
          <input
            type="number"
            min={0}
            value={nova.cota_gb}
            onChange={(e) => setNova((n) => ({ ...n, cota_gb: e.target.value }))}
            placeholder="Cota (GB)"
            aria-label="Cota em GB"
            className="rounded-lg border border-linha px-3 py-2 text-sm"
          />
          {/* O NÍVEL NA CRIAÇÃO. A API já aceitava (`POST /caixas` recebe
              `nivel`, com a trava em caixas.go:267); era só a tela que não
              mandava — e por isso criar um administrador eram dois passos,
              criar e depois editar.

              A lista mostra todos os níveis de propósito. Quem recusa
              promoção acima do próprio nível é a API, em toda requisição;
              esconder opção aqui seria desenho, não segurança, e esconder
              demais impediria o super admin de fazer o que pode. É a mesma
              decisão do formulário de edição. */}
          <select
            value={nova.nivel}
            onChange={(e) => setNova((n) => ({ ...n, nivel: e.target.value }))}
            aria-label="Nível de acesso"
            className="rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
          >
            <option value="usuario">Usuário</option>
            <option value="admin_dominio">Admin do domínio</option>
            <option value="revenda">Gestor de revenda</option>
            <option value="super_admin">Super admin</option>
          </select>
          {/* Trava o envio em vez de deixar a API recusar com 422: a regra
              é conhecida aqui, e fazer a ida e volta para ouvir o que já se
              sabia é só tempo perdido de quem está cadastrando dez caixas
              seguidas. O mínimo vem do DOMÍNIO digitado (12, ou 4 com senha
              simples); domínio que não está na lista fica com 12, e a API
              continua sendo quem decide. */}
          <Botao
            tipo="submit"
            ocupado={criarCaixa.isPending}
            desabilitado={nova.senha.trim().length < minimoDaNova}
          >
            Criar
          </Botao>

          {/* A SENHA AGORA SE DIGITA.
              Antes ela era só gerada e MOSTRADA — não havia campo. A geração
              continua, porque é o caminho certo na maioria das vezes e
              pouparia quem não quer pensar numa senha. Mas quem tem motivo
              para escolher — o cliente que pediu uma específica, a caixa que
              vai num aparelho onde digitar é sofrido — não tinha como.
              Sugerir não é impor. */}
          <div className="sm:col-span-4">
            <label className="block text-xs text-tinta-fraca">
              Senha inicial
              <span className="mt-1 flex flex-wrap items-center gap-2">
                <input
                  value={nova.senha}
                  onChange={(e) => setNova((n) => ({ ...n, senha: e.target.value }))}
                  // `text`, e não `password`: esta senha vai ser DITADA ou
                  // copiada para o cliente agora. Esconder o que quem digita
                  // precisa conferir e repassar só produz erro de digitação
                  // que aparece depois, no primeiro login que falha.
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  aria-label="Senha inicial da caixa"
                  className="min-w-0 flex-1 rounded-lg border border-linha px-3 py-2 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setNova((n) => ({ ...n, senha: senhaInicial() }))}
                  className="rounded-full border border-linha px-3 py-2 text-xs text-tinta-media"
                >
                  gerar outra
                </button>
              </span>
            </label>
            <p className="mt-1 text-xs text-tinta-fraca">
              {nova.senha.length > 0 && nova.senha.length < minimoDaNova ? (
                <span className="text-perigo">
                  faltam {minimoDaNova - nova.senha.length} caracteres — o mínimo é{" "}
                  {minimoDaNova}
                </span>
              ) : (
                <>
                  Mínimo de {minimoDaNova} caracteres
                  {minimoDaNova < 12 ? " (este domínio aceita senha simples)" : ""}. A
                  sugerida evita caracteres que se confundem ao ditar (l/I/1, O/0) e
                  vem em blocos, porque é assim que ela costuma ser entregue.
                </>
              )}
            </p>
          </div>

          <p className="text-xs text-tinta-fraca sm:col-span-4">
            Cota vazia herda a do domínio; 0 é ilimitada. A senha aparece
            novamente na confirmação, uma única vez — depois daqui só existe o
            hash, e nem nós conseguimos mostrá-la de novo.
          </p>
        </form>
      )}

      {criando === "alias" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            criarAlias.mutate();
          }}
          className="mb-5 grid gap-2 rounded-xl border border-linha bg-fundo p-4 sm:grid-cols-[1fr_1fr_auto]"
        >
          {/* O CURINGA PRECISA SER DITO, senão não é encontrado.
              A API aceita `@dominio` desde sempre — a validação dela diz
              "caixa@dominio ou @dominio" — e o motor tem a proteção que
              impede o curinga roubar mensagem de caixa real. Mas o campo
              mostrava só `vendas@dominio.com.br`, e quem quisesse um
              catch-all concluiria que não dá. */}
          <input
            value={novoAlias.origem}
            onChange={(e) => setNovoAlias((a) => ({ ...a, origem: e.target.value }))}
            placeholder="vendas@dominio.com.br  ·  @dominio.com.br = curinga"
            aria-label="Endereço de origem"
            title="Use @dominio.com.br (sem nada antes do @) para pegar tudo que não existe"
            autoFocus
            className="rounded-lg border border-linha px-3 py-2 text-sm"
          />
          <input
            value={novoAlias.destino}
            onChange={(e) => setNovoAlias((a) => ({ ...a, destino: e.target.value }))}
            placeholder="quem recebe de verdade — vários, separados por vírgula"
            aria-label="Para onde entregar"
            className="rounded-lg border border-linha px-3 py-2 text-sm"
          />
          <Botao tipo="submit" ocupado={criarAlias.isPending}>
            Criar
          </Botao>

          <div className="text-xs text-tinta-fraca sm:col-span-3">
            {/* O AVISO DE CATCH-ALL APARECE QUANDO ELE É DIGITADO, e não num
                rodapé que se lê depois de criar. Catch-all é a diferença
                entre "recuse quem não existe" e "aceite tudo" — e quem
                aceita tudo vira depósito de spam, porque o endereço nunca
                devolve erro e passa a valer para sempre nas listas. */}
            {ehCatchAll(novoAlias.origem) ? (
              <p className="rounded-lg border border-aviso/40 bg-aviso/10 px-3 py-2 text-tinta">
                <strong>Isto é um catch-all.</strong> Todo e-mail enviado a
                qualquer endereço de <strong>{novoAlias.origem.trim()}</strong> que
                não seja uma caixa existente vai parar aí — inclusive erro de
                digitação e varredura de spam. O endereço deixa de recusar
                mensagem, e quem varre a internet descobre isso rápido.
              </p>
            ) : (
              <p>
                Para <strong>vários destinos</strong>, separe por vírgula — vira um
                encaminhador por destino, e dá para remover um sem mexer nos outros.
                <br />
                Origem <code>@dominio.com.br</code>, sem nada antes do arroba, cria um{" "}
                <strong>catch-all</strong>.
              </p>
            )}
            {contarDestinos(novoAlias.destino) > 1 && (
              <p className="mt-1 text-acao">
                serão criados {contarDestinos(novoAlias.destino)} encaminhadores, um
                para cada destino
              </p>
            )}
          </div>
        </form>
      )}

      {erro && <p className="mb-4 text-sm text-perigo">{erro}</p>}

      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Filtrar por endereço"
        aria-label="Filtrar caixas"
        className="mb-4 w-full max-w-sm rounded-lg border border-linha px-3 py-2 text-sm"
      />

      {/* AGRUPADO POR DOMÍNIO, e plano quando se está buscando.
          Uma lista corrida de caixas é ilegível com vários clientes: os
          endereços de empresas diferentes se intercalam por ordem
          alfabética, e achar "as caixas do fulano" vira trabalho de olho.
          Buscar é outra pergunta — quem digita um trecho quer o endereço,
          não a casa dele — e aí a lista volta a ser plana. */}
      {busca ? (
        <>
          <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
            <TabelaDeCaixas
              itens={lista.data?.itens ?? []}
              podeAdministrar={podeVer("admin_dominio")}
              editando={editando}
              setEditando={setEditando}
              aoApagar={(c) => {
                remover.reset();
                setARemover({ uuid: c.uuid!, email: c.email! });
              }}
              aoMostrarSenha={(email, senha) =>
                setSenhaMostrada({ email, senha, motivo: "redefinida" })
              }
            />
          </Estado>
          <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="caixas" />
        </>
      ) : (
        <Estado consulta={listaDominios} vazio={(listaDominios.data?.itens?.length ?? 0) === 0}>
          <div className="space-y-2">
            {(listaDominios.data?.itens ?? []).map((d) => (
              <GrupoDoDominio
                key={d.uuid}
                dominio={d}
                // UM DOMÍNIO SÓ ABRE SOZINHO. Quem administra um domínio
                // apenas não deveria ter que clicar para ver a própria
                // lista — o agrupamento existe para quem tem muitos.
                abertoDeInicio={(listaDominios.data?.itens?.length ?? 0) === 1}
                podeAdministrar={podeVer("admin_dominio")}
                editando={editando}
                setEditando={setEditando}
                aoApagar={(c) => {
                  remover.reset();
                  setARemover({ uuid: c.uuid!, email: c.email! });
                }}
                aoMostrarSenha={(email, senha) =>
                  setSenhaMostrada({ email, senha, motivo: "redefinida" })
                }
              />
            ))}
          </div>
        </Estado>
      )}


      {aRemover && (
        <ConfirmarRemocao
          titulo="Apagar caixa"
          frase={aRemover.email}
          rotulo="Apagar a conta"
          ocupado={remover.isPending}
          erro={remover.error ? mensagemDoErro(remover.error) : undefined}
          aoCancelar={() => setARemover(null)}
          aoConfirmar={() => remover.mutate(aRemover)}
          oQueAcontece={
            <>
              <p>
                Some a conta, o acesso e o índice de <strong>{aRemover.email}</strong>.
              </p>
              <p className="text-tinta-fraca">
                As mensagens NÃO são apagadas junto: elas ficam no servidor, e a
                plataforma passa a recusar recriar este endereço até alguém
                decidir se elas voltam para a mesma pessoa ou vão para o lixo.
                É o que impede que quem assumir o endereço abra a caixa de quem
                saiu. Quem resolve isso é o administrador do servidor, com{" "}
                <code>make maildirs-orfaos</code>.
              </p>
            </>
          }
        />
      )}

      {/* "Encaminhadores", e não "aliases". `alias` é o nome que o Postfix
          dá; quem opera veio do cPanel, onde isto se chama Encaminhadores —
          e foi procurando por essa palavra que o Jhou não achou a tela que
          já existia. A mensagem de erro do próprio servidor já dizia
          "encaminhamento"; o menu é que estava falando outra língua. */}
      <h2 className="mt-8 mb-3 text-sm font-medium text-tinta">Encaminhadores</h2>
      <Estado consulta={listaAliases} vazio={listaAliases.data?.itens?.length === 0}>
        <Tabela colunas={["Recebe em", "Entrega para", "Situação", ""]}>
          {listaAliases.data?.itens?.map((a) => (
            <LinhaDoAlias key={a.uuid} alias={a} podeAdministrar={podeVer("admin_dominio")} />
          ))}
        </Tabela>
      </Estado>
      <Paginacao pagina={paginaAliases} dados={listaAliases.data} aoMudar={setPaginaAliases} nome="aliases" />

    </>
  );
}

/**
 * Um alias na tabela.
 *
 * DEFEITO REAL: a tela só CRIAVA e listava. A API tem `alterar` e `remover`
 * desde a fase 3, e encaminhamento que não se remove é problema concreto —
 * a pessoa sai da empresa e o alias continua entregando na caixa dela.
 *
 * DESLIGAR existe ao lado de apagar, e não é redundância: desligar é
 * reversível e guarda para onde o alias apontava. Quem desliga por suspeita
 * quase sempre quer ligar de volta depois de entender o que aconteceu.
 */
function LinhaDoAlias({ alias, podeAdministrar }: { alias: Alias; podeAdministrar: boolean }) {
  const fila = useQueryClient();
  const [destino, setDestino] = useState(alias.destino ?? "");
  const [editando, setEditando] = useState(false);
  const recarregar = () => void fila.invalidateQueries({ queryKey: ["p"] });

  const alternar = useMutation({
    mutationFn: () => aliases.alterar(alias.uuid!, { ativo: !alias.ativo } as never),
    onSuccess: recarregar,
  });
  const salvar = useMutation({
    mutationFn: () => aliases.alterar(alias.uuid!, { destino: destino.trim() } as never),
    onSuccess: () => {
      setEditando(false);
      recarregar();
    },
  });
  const apagar = useMutation({
    mutationFn: () => aliases.remover(alias.uuid!),
    onSuccess: recarregar,
  });

  return (
    <>
      <Linha>
        <Celula>
          {alias.origem}
          {/* QUAL DELES É O CURINGA, na lista. A API já devolve `catch_all`
              e a tela ignorava. Numa lista de quarenta encaminhadores, o que
              pega TUDO que não existe é o único que muda o comportamento do
              domínio inteiro — e era o único sem marca. */}
          {alias.catch_all && (
            <span className="ml-2 rounded-full bg-aviso/20 px-1.5 py-px align-middle text-[9px] font-bold uppercase tracking-wider text-tinta-media">
              curinga
            </span>
          )}
        </Celula>
        <Celula>
          {editando ? (
            <input
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              aria-label="Para onde o encaminhador entrega"
              className="w-full rounded-lg border border-linha px-2 py-1 text-sm"
            />
          ) : (
            alias.destino
          )}
        </Celula>
        <Celula>
          <Selo texto={alias.ativo ? "ativo" : "desligado"} tom={alias.ativo ? "ok" : "neutro"} />
        </Celula>
        <Celula>
          {podeAdministrar && (
            <span className="flex gap-3 whitespace-nowrap">
              {editando ? (
                <>
                  <button
                    onClick={() => salvar.mutate()}
                    disabled={salvar.isPending}
                    className="text-xs text-acao underline disabled:opacity-50"
                  >
                    salvar
                  </button>
                  <button
                    onClick={() => {
                      setDestino(alias.destino ?? "");
                      setEditando(false);
                    }}
                    className="text-xs text-tinta-fraca underline"
                  >
                    cancelar
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditando(true)} className="text-xs text-acao underline">
                    editar
                  </button>
                  <button
                    onClick={() => alternar.mutate()}
                    disabled={alternar.isPending}
                    className="text-xs text-acao underline disabled:opacity-50"
                  >
                    {alias.ativo ? "desligar" : "ligar"}
                  </button>
                  <button
                    onClick={() => apagar.mutate()}
                    disabled={apagar.isPending}
                    className="text-xs text-perigo underline disabled:opacity-50"
                    title="Apagar um alias não apaga mensagem nenhuma — ele só deixa de encaminhar."
                  >
                    apagar
                  </button>
                </>
              )}
            </span>
          )}
        </Celula>
      </Linha>
      {(salvar.error || apagar.error || alternar.error) && (
        <tr>
          <td colSpan={4} className="px-4 pb-2 text-sm text-perigo">
            {mensagemDoErroGeral(salvar.error ?? apagar.error ?? alternar.error)}
          </td>
        </tr>
      )}
    </>
  );
}

function mensagemDoErroGeral(e: unknown): string {
  return e instanceof ErroDaAPI
    ? (Object.values(e.porCampo)[0]?.[0] ?? e.mensagem)
    : explicar(e);
}

/**
 * Uma caixa na tabela, com edição e redefinição de senha embutidas.
 *
 * DEFEITO REAL, relatado pelo Jhou: o painel só CRIAVA e APAGAVA caixa. Cota,
 * nível, situação e senha — tudo que a API já aceitava — não tinha tela. Para
 * uma plataforma de e-mail isso deixa de fora a operação mais comum que
 * existe: redefinir a senha de quem esqueceu.
 */
function LinhaDaCaixa({
  caixa,
  podeAdministrar,
  editando,
  aoEditar,
  aoFechar,
  aoApagar,
  aoMostrarSenha,
}: {
  caixa: Caixa;
  podeAdministrar: boolean;
  editando: boolean;
  aoEditar: () => void;
  aoFechar: () => void;
  aoApagar: () => void;
  aoMostrarSenha: (email: string, senha: string) => void;
}) {
  const fila = useQueryClient();
  type Nivel = NonNullable<Caixa["nivel"]>;
  const [campos, setCampos] = useState({
    nome_exibicao: caixa.nome_exibicao ?? "",
    cota_gb: emGB(caixa.cota_bytes),
    nivel: (caixa.nivel ?? "usuario") as Nivel,
    ativo: caixa.ativo ?? true,
  });

  // REDEFINIR TAMBÉM DEIXA DIGITAR.
  //
  // Antes o botão gerava e aplicava no mesmo clique. Duas consequências, e
  // as duas apareceram no uso: quem tinha motivo para escolher a senha — o
  // cliente que pediu uma específica, a política de quem contratou — não
  // tinha como; e uma ação que DERRUBA todas as sessões e senhas de
  // aplicativo acontecia sem chance de voltar atrás.
  //
  // Agora o botão abre o campo, com uma sugestão pronta. Quem só quer a
  // sugerida confirma e segue; quem quer a própria, digita.
  const [novaSenha, setNovaSenha] = useState<string | null>(null);
  // Quem diz o mínimo é a caixa, que o herda do domínio (12, ou 4 com senha
  // simples). Sem o campo — API antiga —, 12, que é o que ela exigia.
  const minimoDaCaixa = caixa.senha_minima ?? 12;

  const salvar = useMutation({
    mutationFn: () =>
      caixas.alterar(caixa.uuid!, {
        nome_exibicao: campos.nome_exibicao.trim(),
        // Vazio devolve a caixa à cota PADRÃO DO DOMÍNIO; 0 é ilimitada.
        // São coisas diferentes, e o campo precisa deixar isso dizer.
        cota_bytes: campos.cota_gb.trim() === "" ? null : emBytes(campos.cota_gb),
        nivel: campos.nivel,
        ativo: campos.ativo,
      } as never),
    onSuccess: () => {
      aoFechar();
      void fila.invalidateQueries({ queryKey: ["p"] });
    },
  });

  const redefinir = useMutation({
    mutationFn: async () => {
      const senha = (novaSenha ?? "").trim();
      await caixas.trocarSenha(caixa.uuid!, senha);
      return senha;
    },
    onSuccess: (senha) => {
      setNovaSenha(null);
      aoMostrarSenha(caixa.email!, senha);
    },
  });

  // A cota EFETIVA, não a própria da caixa. Uma caixa com cota nula HERDA a
  // do domínio — mostrar "ilimitado" ali dizia o contrário do que acontece, e
  // erra para o lado caro: o operador acha que não há teto e a entrega começa
  // a recusar sem ninguém entender por quê.
  const efetiva = caixa.cota_efetiva_bytes ?? 0;
  const herdada = caixa.cota_bytes === null || caixa.cota_bytes === undefined;

  return (
    <>
      <Linha>
        <Celula>{caixa.email}</Celula>
        <Celula>{caixa.nome_exibicao || "—"}</Celula>
        <Celula>
          <Selo texto={caixa.nivel ?? "usuario"} tom="neutro" />
          {caixa.dois_fatores && (
            <span className="ml-1 text-xs text-acao" title="Segundo fator ativo">
              2FA
            </span>
          )}
        </Celula>
        <Celula>
          {tamanho(caixa.cota_usada_bytes)} de {efetiva ? tamanho(efetiva) : "ilimitado"}
          {herdada && efetiva > 0 && (
            <span className="block text-xs text-tinta-fraca">herdada do domínio</span>
          )}
        </Celula>
        <Celula>
          <Selo texto={caixa.ativo ? "ativa" : "suspensa"} tom={caixa.ativo ? "ok" : "aviso"} />
        </Celula>
        <Celula>
          {/* AÇÕES COMO ÍCONES, e não como texto sublinhado solto na linha.
              A versão anterior misturava uma PERGUNTA ("entrar na caixa?")
              com três links na mesma célula, e o resultado era um amontoado
              onde "apagar" ficava a um pixel de "editar". Ícone com área de
              toque própria, rótulo no title e no aria-label, e a confirmação
              fora da linha. */}
          {podeAdministrar && (
            <span className="flex items-center justify-end gap-1 whitespace-nowrap">
              <EntrarNaCaixa caixa={caixa} />
              <BotaoDeAcao
                icone={editando ? "X" : "Pencil"}
                rotulo={editando ? "Fechar a edição" : "Editar esta caixa"}
                aoClicar={aoEditar}
              />
              <BotaoDeAcao
                icone="Trash2"
                rotulo="Apagar esta caixa"
                tom="perigo"
                aoClicar={aoApagar}
              />
            </span>
          )}
        </Celula>
      </Linha>

      {editando && (
        <tr>
          <td colSpan={6} className="bg-fundo px-4 py-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                salvar.mutate();
              }}
              className="grid gap-3 sm:grid-cols-3"
            >
              <Campo
                rotulo="Nome de exibição"
                valor={campos.nome_exibicao}
                aoMudar={(v) => setCampos((c) => ({ ...c, nome_exibicao: v }))}
              />
              <Campo
                rotulo="Cota (GB)"
                tipo="number"
                valor={campos.cota_gb}
                aoMudar={(v) => setCampos((c) => ({ ...c, cota_gb: v }))}
                dica="Vazio = herda do domínio. 0 = ilimitada."
              />
              <label className="text-xs text-tinta-fraca">
                Nível
                <select
                  value={campos.nivel}
                  onChange={(e) => setCampos((c) => ({ ...c, nivel: e.target.value as Nivel }))}
                  className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
                >
                  <option value="usuario">usuario</option>
                  <option value="admin_dominio">admin_dominio</option>
                  <option value="revenda">revenda</option>
                  <option value="super_admin">super_admin</option>
                </select>
                {/* A lista mostra todos; quem recusa promoção acima do próprio
                    nível é a API, em toda requisição. Esconder opções aqui
                    seria desenho, não segurança — e esconder demais impediria
                    o super admin de fazer o que pode. */}
                <span className="mt-1 block">Não é possível promover acima do seu nível.</span>
              </label>

              <label className="flex items-center gap-2 text-sm text-tinta">
                <input
                  type="checkbox"
                  checked={campos.ativo}
                  onChange={(e) => setCampos((c) => ({ ...c, ativo: e.target.checked }))}
                />
                Caixa ativa
                <span className="text-xs text-tinta-fraca">
                  (suspensa para de receber e de autenticar)
                </span>
              </label>

              <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                <Botao tipo="submit" icone="CheckCircle2" ocupado={salvar.isPending}>
                  Salvar
                </Botao>
                <Botao tom="neutro" aoClicar={aoFechar}>
                  Cancelar
                </Botao>
                {novaSenha === null ? (
                  <>
                    <Botao
                      tom="neutro"
                      icone="Lock"
                      aoClicar={() => setNovaSenha(senhaInicial())}
                    >
                      Redefinir senha
                    </Botao>
                    <span className="text-xs text-tinta-fraca">
                      Redefinir DERRUBA todas as sessões e senhas de aplicativo desta
                      caixa.
                    </span>
                  </>
                ) : (
                  <span className="flex w-full flex-wrap items-center gap-2">
                    <input
                      // Texto visível, como na criação: esta senha vai ser
                      // ditada ou copiada agora, e esconder o que se precisa
                      // conferir só gera erro de digitação que aparece no
                      // primeiro login que falha.
                      type="text"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      spellCheck={false}
                      autoComplete="off"
                      autoFocus
                      aria-label="Nova senha da caixa"
                      className="min-w-0 flex-1 rounded-lg border border-linha px-3 py-2 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setNovaSenha(senhaInicial())}
                      className="rounded-full border border-linha px-3 py-2 text-xs text-tinta-media"
                    >
                      gerar outra
                    </button>
                    <Botao
                      tom="perigo"
                      icone="Lock"
                      ocupado={redefinir.isPending}
                      desabilitado={novaSenha.trim().length < minimoDaCaixa}
                      aoClicar={() => redefinir.mutate()}
                    >
                      Confirmar
                    </Botao>
                    <button
                      type="button"
                      onClick={() => setNovaSenha(null)}
                      className="text-xs text-tinta-media underline"
                    >
                      cancelar
                    </button>
                    <span className="w-full text-xs text-tinta-fraca">
                      {novaSenha.trim().length < minimoDaCaixa ? (
                        <span className="text-perigo">
                          faltam {minimoDaCaixa - novaSenha.trim().length} caracteres — o
                          mínimo é {minimoDaCaixa}
                        </span>
                      ) : (
                        <>
                          Ao confirmar, <strong>todas as sessões e senhas de
                          aplicativo</strong> desta caixa são derrubadas.
                        </>
                      )}
                    </span>
                  </span>
                )}
                {(salvar.error || redefinir.error) && (
                  <p className="w-full text-sm text-perigo">
                    {explicarErro(salvar.error ?? redefinir.error)}
                  </p>
                )}
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

function explicarErro(e: unknown): string {
  return e instanceof ErroDaAPI
    ? (Object.values(e.porCampo)[0]?.[0] ?? e.mensagem)
    : explicar(e);
}

/** Bytes → GB para o campo; nulo vira vazio, que significa "herda". */
function emGB(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes === 0) return "0";
  return String(Math.round((bytes / 1024 ** 3) * 100) / 100);
}

function emBytes(gb: string): number {
  const n = Number.parseFloat(gb.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1024 ** 3) : 0;
}

// ---------------------------------------------------------------------------

const TOM_DO_EVENTO: Record<string, "ok" | "aviso" | "erro" | "neutro"> = {
  entregue: "ok",
  recebido: "neutro",
  adiado: "aviso",
  rejeitado: "erro",
  bounce: "erro",
  descartado: "neutro",
  quarentena: "aviso",
};

export function Entregas() {
  // O FILTRO MORA NA URL, não em `useState`.
  //
  // A API aceita estes três desde a fase 3 e a tela ignorava. Na pergunta que
  // mais chega ao suporte — "por que o e-mail do fulano não chegou?" — é a
  // diferença entre procurar pelo endereço e rolar cinquenta linhas.
  //
  // Na URL porque os cartões da visão geral LEVAM para cá já filtrados, e
  // porque o endereço da tela passou a ser a resposta que o suporte manda.
  const [f, definir] = useFiltroNaURL(["tipo", "endereco", "desde", "ate"] as const);
  const { tipo, endereco, desde, ate } = f;
  const filtro = [tipo, endereco, desde, ate].join("|");
  const [pagina, setPagina] = usePagina(filtro);
  const lista = useQuery({
    queryKey: ["p", "entregas", filtro, pagina],
    queryFn: () =>
      entregas.listar({
        tipo: tipo || undefined,
        endereco: endereco.trim() || undefined,
        desde: desde || undefined,
        ate: ate || undefined,
        pagina,
        por_pagina: 50,
      }),
    refetchInterval: 20_000,
  });
  const naFila = useQuery({ queryKey: ["p", "fila", "lista"], queryFn: () => entregas.fila() });

  return (
    <>
      <Cabecalho
        titulo="Fila e entregas"
        descricao="O que entrou e saiu, e o que ainda não conseguiu sair."
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <Indicador
          rotulo="Presas na fila"
          valor={naFila.data?.total ?? "—"}
          detalhe="ainda tentando"
          alerta={(naFila.data?.total ?? 0) > 0}
        />
      </div>

      <div className="mb-3 grid gap-2 sm:grid-cols-[2fr_1fr_1fr]">
        <input
          value={endereco}
          onChange={(e) => definir("endereco", e.target.value)}
          placeholder="Filtrar por endereço (de ou para)"
          aria-label="Filtrar por endereço"
          className="rounded-lg border border-linha px-3 py-2 text-sm"
        />
        <label className="text-xs text-tinta-fraca">
          De
          <input
            type="date"
            value={desde}
            onChange={(e) => definir("desde", e.target.value)}
            className="w-full rounded-lg border border-linha px-3 py-1.5 text-sm text-tinta"
          />
        </label>
        <label className="text-xs text-tinta-fraca">
          Até
          <input
            type="date"
            value={ate}
            onChange={(e) => definir("ate", e.target.value)}
            className="w-full rounded-lg border border-linha px-3 py-1.5 text-sm text-tinta"
          />
        </label>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {["", "entregue", "adiado", "rejeitado", "bounce"].map((t) => (
          <button
            key={t || "todos"}
            onClick={() => definir("tipo", t)}
            className={`rounded-full px-3 py-1 text-xs ${
              tipo === t ? "bg-acao text-white" : "border border-linha text-tinta-media"
            }`}
          >
            {rotuloDoTipoDeEntrega(t)}
          </button>
        ))}
      </div>

      <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
        <Tabela colunas={["Quando", "Tipo", "De", "Para", "O que aconteceu"]}>
          {lista.data?.itens?.map((e, i) => (
            <Linha key={(e.uuid ?? "") + i}>
              <Celula>{dataCurta(e.ocorrido_em)}</Celula>
              <Celula>
                {/* O MESMO rótulo das pastilhas de filtro.
                    A tradução de `bounce` para "devolvido" tinha entrado só
                    nos botões: a coluna continuava dizendo `bounce`, e a
                    pessoa filtrava por "devolvido" para ver uma tabela cheia
                    de inglês. Um nome por coisa. */}
                <Selo
                  texto={rotuloDoTipoDeEntrega(e.tipo ?? "")}
                  tom={TOM_DO_EVENTO[e.tipo ?? ""] ?? "neutro"}
                />
              </Celula>
              <Celula>{e.remetente || "—"}</Celula>
              <Celula>{e.destinatario || "—"}</Celula>
              {/* A explicação vem da API traduzida: "550 5.1.1" não diz nada
                  a quem não lê SMTP, e é o que o cliente vai perguntar. */}
              <Celula>{e.explicacao || e.resposta || "—"}</Celula>
            </Linha>
          ))}
        </Tabela>
      </Estado>
      <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="eventos" />

    </>
  );
}

// ---------------------------------------------------------------------------

export function Quarentena() {
  const fila = useQueryClient();
  const [f, definir] = useFiltroNaURL(["dominio"] as const);
  const dominio = f.dominio;
  const [pagina, setPagina] = usePagina(dominio);
  const lista = useQuery({
    queryKey: ["p", "quarentena", "lista", dominio, pagina],
    queryFn: () =>
      quarentena.listar({ dominio: dominio || undefined, pagina, por_pagina: 50 }),
  });
  const dominiosDisponiveis = useQuery({
    queryKey: ["p", "dominios", "para-filtrar"],
    queryFn: () => dominios.listar({ por_pagina: 200 }),
  });
  const recarregar = () => void fila.invalidateQueries({ queryKey: ["p"] });
  const liberar = useMutation({ mutationFn: quarentena.liberar, onSuccess: recarregar });
  const descartar = useMutation({ mutationFn: quarentena.descartar, onSuccess: recarregar });

  return (
    <>
      <Cabecalho
        titulo="Antispam e quarentena"
        descricao="Retidas por suspeita de spam. A decisão é de quem recebe — aqui você só ajuda."
      />

      {/* Uma revenda com trinta domínios precisa olhar um de cada vez: a
          quarentena de um cliente não diz nada sobre a do outro. */}
      <select
        value={dominio}
        onChange={(e) => definir("dominio", e.target.value)}
        aria-label="Filtrar por domínio"
        className="mb-4 rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
      >
        <option value="">Todos os domínios</option>
        {dominiosDisponiveis.data?.itens?.map((d) => (
          <option key={d.uuid} value={d.uuid ?? ""}>
            {d.nome}
          </option>
        ))}
      </select>

      {/* O AVISO QUE EXPLICA A COLUNA VAZIA.
          Sem ele, uma quarentena com milhares de "—" parece defeito. A
          explicação é uma linha e vale para a tela inteira, então ela fica
          aqui e não repetida em cada mensagem. Só aparece quando há o que
          explicar. */}
      {/* O NÚMERO VEM DO SERVIDOR, não da página.
          A primeira versão contava `lista.data.itens`, que são só as 50 da
          página — e o aviso dizia "47 mensagens vieram de migração" quando
          eram 13.053 de 13.126. Número certo sobre a página e errado como
          resposta à pergunta que quem lê está fazendo. */}
      {(lista.data?.sem_nota ?? 0) > 0 && (
        <p className="mb-3 rounded-lg border border-linha bg-fundo px-3 py-2 text-xs text-tinta-media">
          <strong className="text-tinta">
            {lista.data!.sem_nota!.toLocaleString("pt-BR")}{" "}
            {lista.data!.sem_nota === 1 ? "mensagem veio" : "mensagens vieram"} de migração
          </strong>{" "}
          e aparece{lista.data!.sem_nota === 1 ? "" : "m"} sem pontuação. Elas foram
          separadas como spam pelo servidor ANTERIOR e entraram aqui direto na caixa,
          sem passar pelo nosso filtro — então nunca receberam nota nossa. Isso é
          esperado, e não quer dizer nota zero.
        </p>
      )}

      <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
        {/* CARTOES NO CELULAR, TABELA NO RESTO.
            A tabela tem sete colunas e rola de lado. Num telefone isso
            esconde justamente as duas que importam: Pontos — que o aviso
            acima explica — e os botoes de liberar e descartar, que sao o
            motivo de a tela existir. Visto numa captura de 20/10/2026: a
            coluna "Para" aparecia cortada e nada depois dela.

            Rolar de lado nao e defeito numa tabela de muitos dados; e
            defeito quando a ACAO esta do lado escondido. */}
        {/* `grid-cols-1` E NAO SO `grid`, e a diferenca e a tela inteira.
            Uma coluna de grade automatica tem largura MINIMA de conteudo. O
            assunto usa `truncate`, que poe `white-space: nowrap` e o torna
            uma linha indivisivel — entao a coluna crescia para caber o
            assunto inteiro, e o cartao ficava mais largo que o telefone. O
            botao "descartar" sumia na borda direita, visto em 20/10/2026.
            `grid-cols-1` do Tailwind usa `minmax(0, 1fr)`, que autoriza a
            coluna a encolher e faz o `truncate` finalmente truncar. */}
        <div className="grid grid-cols-1 gap-2 md:hidden">
          {lista.data?.itens?.map((m) => (
            <article
              key={m.uuid}
              className="min-w-0 rounded-xl border border-linha bg-white p-3"
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <strong className="min-w-0 flex-1 truncate text-sm text-tinta">
                  {m.remetente}
                </strong>
                {m.pontuacao == null ? (
                  <span
                    className="rounded-full bg-linha px-2 py-0.5 text-xs text-tinta-media"
                    title="Nao avaliada pelo nosso filtro — veio de migracao"
                  >
                    sem nota
                  </span>
                ) : (
                  <span
                    className="rounded-full bg-aviso/15 px-2 py-0.5 text-xs tabular-nums text-aviso"
                    title={`Nota do antispam. A partir de ${NOTA_DE_RECUSA} a mensagem é recusada na entrega.`}
                  >
                    {m.pontuacao.toFixed(1)} de {NOTA_DE_RECUSA}
                  </span>
                )}
                <span className="text-xs text-tinta-fraca">{dataCurta(m.recebida_em)}</span>
              </div>

              <p className="mt-1 truncate text-sm text-tinta-media">
                {m.assunto || "(sem assunto)"}
              </p>
              <p className="mt-0.5 truncate text-xs text-tinta-fraca">
                para {m.destinatario}
              </p>

              {(m.motivos ?? []).length > 0 && (
                <p className="mt-1 truncate text-xs text-tinta-fraca">
                  {(m.motivos ?? []).slice(0, 3).join(", ")}
                </p>
              )}

              {/* Com altura de toque: dois links de 12px lado a lado viram
                  alvo pequeno demais no telefone. */}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => liberar.mutate(m.uuid!)}
                  className="flex-1 rounded-full bg-acao px-3 py-2 text-xs font-medium text-white"
                >
                  liberar
                </button>
                <button
                  onClick={() => descartar.mutate(m.uuid!)}
                  className="flex-1 rounded-full border border-linha px-3 py-2 text-xs text-perigo"
                >
                  descartar
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* NENHUMA CÉLULA DESTA TABELA TINHA TETO DE LARGURA.
            O assunto de um spam é longo por desenho — é onde mora a isca —,
            e sem teto ele empurrava "Pontos", "Por quê" e os dois botões
            para fora da área visível. A revisão de UX viu os dois sintomas
            e eles são o mesmo: o assunto quebrando a linha e o "Por quê"
            cortado. Quem opera esta tela veio decidir liberar ou descartar,
            e a decisão estava justamente na parte empurrada para fora.

            `table-fixed` + larguras declaradas: sem `fixed`, o navegador
            dimensiona pelo CONTEÚDO e um `max-w` numa célula não segura a
            coluna. Com ele, o `truncate` finalmente trunca. */}
        <div className="hidden md:block">
        <Tabela
          fixa
          colunas={["Quando", "De", "Para", "Assunto", "Pontos", "Por quê", ""]}
          larguras={["w-28", "w-[18%]", "w-[18%]", "w-auto", "w-28", "w-[22%]", "w-36"]}
        >
          {lista.data?.itens?.map((m) => (
            <Linha key={m.uuid}>
              <Celula>{dataCurta(m.recebida_em)}</Celula>
              {/* `title` em tudo que trunca: a informação inteira continua
                  alcançável, só deixa de arrastar a tabela. */}
              <Celula>
                <span className="block truncate" title={m.remetente ?? undefined}>
                  {m.remetente}
                </span>
              </Celula>
              <Celula>
                <span className="block truncate" title={m.destinatario ?? undefined}>
                  {m.destinatario}
                </span>
              </Celula>
              <Celula>
                <span className="block truncate" title={m.assunto || "(sem assunto)"}>
                  {m.assunto || "(sem assunto)"}
                </span>
              </Celula>
              <Celula>
                {/* SEM NOTA NÃO É NOTA BAIXA.
                    `null` quer dizer que o nosso filtro nunca viu esta
                    mensagem — é o caso de tudo que veio por migração, que
                    entra na caixa por IMAP sem passar por Postfix nem
                    Rspamd. Mostrar "0.0" ali dizia o oposto da verdade:
                    zero é "olhei e está limpa", e a mensagem está na
                    quarentena. */}
                {m.pontuacao == null ? (
                  <span
                    className="text-tinta-fraca"
                    title="Não avaliada pelo nosso filtro — veio de migração"
                  >
                    —
                  </span>
                ) : (
                  <span
                    className="tabular-nums"
                    title={`A partir de ${NOTA_DE_RECUSA} a mensagem é recusada na entrega.`}
                  >
                    {m.pontuacao.toFixed(1)} de {NOTA_DE_RECUSA}
                  </span>
                )}
              </Celula>
              <Celula>
                {/* Só os três que mais pesaram NA TELA: a lista inteira de
                    símbolos faria o operador parar de ler os que importam.
                    Mas o `title` leva TODOS — quem está investigando um
                    falso positivo quer os de baixo também, e antes eles
                    simplesmente não existiam em lugar nenhum da interface. */}
                <span
                  className="block truncate text-xs text-tinta-fraca"
                  title={(m.motivos ?? []).join(", ") || undefined}
                >
                  {(m.motivos ?? []).slice(0, 3).join(", ") || "—"}
                </span>
              </Celula>
              <Celula>
                {/* Mesmo alvo de toque dos cartões do celular. Eram dois
                    sublinhados de 12px colados, com "descartar" — que não
                    volta — a dois pixels de "liberar". */}
                <span className="flex justify-end gap-1.5 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => liberar.mutate(m.uuid!)}
                    aria-label={`Liberar a mensagem de ${m.remetente} para ${m.destinatario}`}
                    className="min-h-[30px] rounded-full border border-linha px-2.5 py-1 text-xs text-acao hover:bg-acao-clara focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-acao"
                  >
                    liberar
                  </button>
                  <button
                    type="button"
                    onClick={() => descartar.mutate(m.uuid!)}
                    aria-label={`Descartar a mensagem de ${m.remetente} para ${m.destinatario}`}
                    className="min-h-[30px] rounded-full border border-perigo/30 px-2.5 py-1 text-xs text-perigo hover:bg-perigo/10 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-acao"
                  >
                    descartar
                  </button>
                </span>
              </Celula>
            </Linha>
          ))}
        </Tabela>
        </div>
      </Estado>
      <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="mensagens" />

    </>
  );
}

// ---------------------------------------------------------------------------

export function Auditoria() {
  const [f, definir] = useFiltroNaURL(["acao", "ator"] as const);
  const { acao, ator } = f;
  const filtro = acao + "|" + ator;
  const [pagina, setPagina] = usePagina(filtro);
  const lista = useQuery({
    queryKey: ["p", "auditoria", "lista", filtro, pagina],
    queryFn: () =>
      auditoria({
        acao: acao.trim() || undefined,
        ator: ator.trim() || undefined,
        pagina,
        por_pagina: 50,
      }),
  });

  return (
    <>
      <Cabecalho
        titulo="Segurança e auditoria"
        descricao="Quem fez o quê, quando e de onde. Somente acréscimo — nem a API apaga daqui."
      />

      {/* Numa auditoria a pergunta quase nunca é "o que aconteceu": é "o que
          FULANO fez", ou "quem apagou caixa". Sem estes dois campos, a
          resposta era rolar página por página. */}
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <input
          value={ator}
          onChange={(e) => definir("ator", e.target.value)}
          placeholder="Quem (endereço de quem agiu)"
          aria-label="Filtrar por quem agiu"
          className="rounded-lg border border-linha px-3 py-2 text-sm"
        />
        <input
          value={acao}
          onChange={(e) => definir("acao", e.target.value)}
          placeholder="Ação (caixa.remover, dominio.alterar…)"
          aria-label="Filtrar por ação"
          className="rounded-lg border border-linha px-3 py-2 text-sm"
        />
      </div>

      <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
        <Tabela colunas={["Quando", "Quem", "Nível", "Ação", "Resultado", "IP"]}>
          {lista.data?.itens?.map((r) => (
            <Linha key={r.uuid}>
              <Celula>{dataCurta(r.criado_em)}</Celula>
              <Celula>{r.ator}</Celula>
              <Celula>
                <Selo texto={rotuloDoNivel(r.ator_nivel)} tom="neutro" />
              </Celula>
              <Celula>
                {/* O código cru fica no `title`: é o que dá para procurar
                    no log do servidor, e o filtro continua aceitando ele. */}
                <span title={r.acao ?? undefined}>{rotuloDaAcao(r.acao)}</span>
              </Celula>
              <Celula>
                <Selo
                  texto={r.resultado ?? "?"}
                  tom={r.resultado === "sucesso" ? "ok" : r.resultado === "negado" ? "erro" : "aviso"}
                />
              </Celula>
              <Celula>{r.ip || "—"}</Celula>
            </Linha>
          ))}
        </Tabela>
      </Estado>
      <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="registros" />

    </>
  );
}


/**
 * A SETA QUE ENTRA NA CAIXA DO CLIENTE.
 *
 * REVISÃO HUMANA (regra 5) — ver interno/web/personificar.go, que é onde as
 * travas de verdade moram. Esta camada não protege nada: ela só desenha.
 *
 * Só aparece para revenda ou acima, porque é só isso que o servidor aceita.
 * Esconder o botão não é a proteção; a proteção é a rota recusar. Mostrar um
 * botão que sempre dá 403 seria só uma forma ruim de informar.
 *
 * CONFIRMA ANTES. Um clique sem pergunta num botão que abre a correspondência
 * de outra pessoa é fácil demais de dar sem querer, e o registro de auditoria
 * fica igual ao de um acesso proposital — não há como desfazer nem explicar
 * depois.
 */
/** A tabela de caixas, usada pela busca plana e por cada grupo de domínio. */
function TabelaDeCaixas({
  itens,
  podeAdministrar,
  editando,
  setEditando,
  aoApagar,
  aoMostrarSenha,
}: {
  itens: Caixa[];
  podeAdministrar: boolean;
  editando: string | null;
  setEditando: (uuid: string | null) => void;
  aoApagar: (c: Caixa) => void;
  aoMostrarSenha: (email: string, senha: string) => void;
}) {
  return (
    <Tabela colunas={["Caixa", "Nome", "Nível", "Uso", "Situação", ""]}>
      {itens.map((c) => (
        <LinhaDaCaixa
          key={c.uuid}
          caixa={c}
          podeAdministrar={podeAdministrar}
          editando={editando === c.uuid}
          aoEditar={() => setEditando(editando === c.uuid ? null : (c.uuid ?? null))}
          aoFechar={() => setEditando(null)}
          aoApagar={() => aoApagar(c)}
          aoMostrarSenha={aoMostrarSenha}
        />
      ))}
    </Tabela>
  );
}

/**
 * Um domínio e as caixas dele.
 *
 * AS CAIXAS SÓ SÃO BUSCADAS AO ABRIR. Com trezentos domínios, carregar todas
 * de uma vez seriam trezentas consultas na abertura da tela para mostrar uma
 * lista que ninguém vai ler inteira.
 */
function GrupoDoDominio({
  dominio,
  abertoDeInicio,
  podeAdministrar,
  editando,
  setEditando,
  aoApagar,
  aoMostrarSenha,
}: {
  dominio: Dominio;
  abertoDeInicio: boolean;
  podeAdministrar: boolean;
  editando: string | null;
  setEditando: (uuid: string | null) => void;
  aoApagar: (c: Caixa) => void;
  aoMostrarSenha: (email: string, senha: string) => void;
}) {
  const [aberto, setAberto] = useState(abertoDeInicio);
  const [pagina, setPagina] = usePagina();

  const lista = useQuery({
    queryKey: ["p", "caixas", "do-dominio", dominio.uuid, pagina],
    queryFn: () => caixas.listar({ dominio: dominio.uuid, pagina, por_pagina: 50 }),
    enabled: aberto,
  });

  return (
    <section className="overflow-hidden rounded-xl border border-linha">
      <button
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className="flex w-full items-center gap-3 bg-fundo px-4 py-3 text-left hover:bg-linha-suave"
      >
        <Icone
          nome={aberto ? "ChevronDown" : "ChevronRight"}
          tamanho={16}
          className="shrink-0 text-tinta-fraca"
        />
        <Icone nome="Globe" tamanho={16} className="shrink-0 text-acao" />
        <span className="min-w-0 flex-1 truncate font-medium text-tinta">{dominio.nome}</span>
        {/* O TOTAL VEM DO CABEÇALHO DE PAGINAÇÃO, e aparece mesmo fechado:
            "quantas caixas este cliente tem?" é a pergunta que traz a pessoa
            a esta tela, e escondê-la atrás de um clique a adia sem motivo. */}
        {aberto && lista.data && (
          <span className="shrink-0 text-xs tabular-nums text-tinta-fraca">
            {lista.data.total} caixa{lista.data.total === 1 ? "" : "s"}
          </span>
        )}
        {/* Domínio suspenso derruba a entrega de todas as caixas dele —
            é a informação mais importante do cabeçalho quando ela existe. */}
        {dominio.status && dominio.status !== "ativo" && (
          <Selo texto={dominio.status} tom="aviso" />
        )}
      </button>

      {aberto && (
        <div className="px-4 pb-3 pt-1">
          <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
            <TabelaDeCaixas
              itens={lista.data?.itens ?? []}
              podeAdministrar={podeAdministrar}
              editando={editando}
              setEditando={setEditando}
              aoApagar={aoApagar}
              aoMostrarSenha={aoMostrarSenha}
            />
          </Estado>
          <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="caixas" />
        </div>
      )}
    </section>
  );
}

/** Um botão de ação da linha: ícone, com rótulo para quem passa o mouse e
 *  para leitor de tela. Texto sublinhado em sequência vira amontoado. */
function BotaoDeAcao({
  icone,
  rotulo,
  aoClicar,
  tom = "acao",
}: {
  icone: Parameters<typeof Icone>[0]["nome"];
  rotulo: string;
  aoClicar: () => void;
  tom?: "acao" | "perigo";
}) {
  return (
    <button
      onClick={aoClicar}
      title={rotulo}
      aria-label={rotulo}
      className={`grid h-8 w-8 place-items-center rounded-lg hover:bg-fundo ${
        tom === "perigo" ? "text-perigo" : "text-acao"
      }`}
    >
      <Icone nome={icone} tamanho={16} />
    </button>
  );
}

function EntrarNaCaixa({ caixa }: { caixa: Caixa }) {
  const { definir, podeVer } = useSessao();
  const navegar = useNavigate();
  const local = useLocation();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState("");

  const entrar = useMutation({
    mutationFn: () => caixas.entrar(caixa.uuid!),
    onSuccess: (nova) => {
      // DE ONDE EU VIM, para o "Sair e voltar ao painel" devolver a pessoa
      // ao lugar certo. Sem isto ela caía em /correio/INBOX como se tivesse
      // acabado de entrar na própria conta — defeito relatado em 26/10/2026.
      //
      // sessionStorage e não localStorage: é POR ABA, como a vaga da conta.
      // Com localStorage, abrir o painel numa segunda aba sobrescreveria o
      // ponto de retorno da primeira.
      try {
        sessionStorage.setItem("cdw_voltar_para", local.pathname + local.search);
      } catch {
        // Armazenamento bloqueado não pode impedir a personificação; o
        // retorno cai no padrão.
      }
      // `definir` é o mesmo ponto de troca de identidade do login, e por isso
      // LIMPA O CACHE: sem isso o webmail abriria com dados do painel do
      // administrador misturados aos da caixa do cliente.
      definir(nova);
      navegar("/correio/INBOX");
    },
    onError: (e) => setErro(explicar(e)),
  });

  if (!podeVer("revenda")) return null;

  return (
    <>
      <BotaoDeAcao
        icone="LogIn"
        rotulo={`Entrar na caixa de ${caixa.email}`}
        aoClicar={() => setConfirmando(true)}
      />

      {/* A CONFIRMAÇÃO SAIU DA LINHA.
          Antes era "entrar na caixa? sim não" espremido ao lado de "editar"
          e "apagar" — quatro decisões diferentes na mesma célula, uma delas
          irreversível. Aqui ela tem espaço para DIZER o que vai acontecer,
          que é o que importa numa ação que o cliente consegue auditar. */}
      {confirmando && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Entrar na caixa do cliente"
          className="fixed inset-0 z-50 grid place-items-center bg-tinta/40 p-4"
          onClick={() => !entrar.isPending && setConfirmando(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
          >
            <h2 className="text-base font-medium text-tinta">Entrar nesta caixa?</h2>
            <p className="mt-2 text-sm text-tinta-media">
              Você vai ver o e-mail de{" "}
              <strong className="text-tinta">{caixa.email}</strong> como se fosse
              essa pessoa.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-tinta-media">
              <li className="flex gap-2">
                <Icone nome="ScrollText" tamanho={15} className="mt-0.5 shrink-0 text-acao" />
                Fica registrado na auditoria, e o cliente vê esse registro.
              </li>
              <li className="flex gap-2">
                <Icone nome="Send" tamanho={15} className="mt-0.5 shrink-0 text-acao" />
                Tudo que for enviado dali sai no nome dessa pessoa.
              </li>
              <li className="flex gap-2">
                <Icone nome="AlarmClock" tamanho={15} className="mt-0.5 shrink-0 text-acao" />
                O acesso dura 30 minutos e expira sozinho.
              </li>
            </ul>

            {erro && (
              <p className="mt-3 text-sm text-perigo" role="alert">
                {erro}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => {
                  setConfirmando(false);
                  setErro("");
                }}
                disabled={entrar.isPending}
                className="rounded-lg px-4 py-2 text-sm text-tinta-media hover:bg-fundo disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => entrar.mutate()}
                disabled={entrar.isPending}
                className="rounded-lg bg-acao px-4 py-2 text-sm font-medium text-white hover:bg-acao-escura disabled:opacity-50"
              >
                {entrar.isPending ? "entrando…" : "Entrar na caixa"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
